import { APPOINTMENT_ROUTES } from '../../../config/api-routes';
import instance from '../../../config/instance';
import { paramsToQueryString } from '../../../utility/helpers/commonFunction';

/**
 * Get kanban board list for appointments
 * This API fetches appointments organized by status for the kanban board view
 * @param {string} timeZone - The timezone for appointment filtering
 * @returns {Promise} - Kanban board data with appointments grouped by status
 */
export const getKanbanBoardListApi = async (timeZone) => {
    const response = await instance.get(`${APPOINTMENT_ROUTES.KANBAN_BOARD_LIST}?timezone=${timeZone}`);
    return response.data;
};

/**
 * Get appointment details by ID
 * This API fetches detailed information for a specific appointment
 * @param {string} id - The appointment ID
 * @returns {Promise} - Appointment details data
 */
export const getAppointmentDetailsApi = async (id) => {
    const response = await instance.get(`${APPOINTMENT_ROUTES.APPOINTMENT_DETAILS}/${id}`);
    return response.data;
};

/**
 * Get appointment request list with filters
 * This API fetches appointment requests with various filters and pagination
 * @param {object} params - Query parameters for filtering
 * @returns {Promise} - Paginated appointment request list
 */
export const getAppointmentRequestListApi = async (params) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((v) => {
                searchParams.append(key, v);
            });
        } else {
            searchParams.append(key, value);
        }
    });

    const queryString = searchParams.toString().replace(/\+/g, '%20');

    const response = await instance.get(APPOINTMENT_ROUTES.APPOINTMENT_REQUEST_LIST + '?' + queryString);

    return response?.data;
};

/**
 * Get appointment request card counts
 * This API fetches summary counts for appointment request tabs/cards
 * @returns {Promise} - Appointment request counts data
 */
export const getAppointmentRequestCardCountApi = async () => {
    const response = await instance.get(APPOINTMENT_ROUTES.APPOINTMENT_REQUEST_CARD_COUNT);

    return response?.data?.data;
};

/**
 * Update appointment status
 * This API updates the status of an appointment
 * @param {string} id - The appointment ID
 * @param {object} payload - The status update payload
 * @returns {Promise} - Update result
 */
export const updateAppointmentStatusApi = async (id, payload) => {
    const response = await instance.put(`${APPOINTMENT_ROUTES.UPDATE_APPOINTMENT_STATUS}/${id}`, payload);
    return response.data;
};

/**
 * Get appointment list with filters
 * This API fetches appointments with various filters and pagination
 * @param {object} params - Query parameters for filtering
 * @returns {Promise} - Paginated appointment list
 */
export const getAppointmentListApi = async (params) => {
    const queryString = paramsToQueryString(params);
    const response = await instance.get(APPOINTMENT_ROUTES.APPOINTMENT_LIST + queryString);
    return response?.data?.data;
};

/**
 * Create new appointment
 * This API creates a new appointment
 * @param {object} params - Query parameters (dontSendFCMNotificationToCustomer, dontSendEmailToCustomer)
 * @param {object} payload - The appointment data
 * @returns {Promise} - Created appointment data
 */
export const createAppointmentApi = async ({ params = {}, payload }) => {
    const queryString = paramsToQueryString(params);
    const url = queryString ? `${APPOINTMENT_ROUTES.CREATE_APPOINTMENT_SAAS}${queryString}` : APPOINTMENT_ROUTES.CREATE_APPOINTMENT_SAAS;
    const response = await instance.post(url, payload);
    return response.data;
};

/**
 * Reschedule appointment
 * This API reschedules an existing appointment
 * @param {string} id - The appointment ID
 * @param {object} payload - The reschedule data
 * @returns {Promise} - Reschedule result
 */
export const rescheduleAppointmentApi = async (id, payload) => {
    const response = await instance.put(`${APPOINTMENT_ROUTES.RESCHEDULE_APPOINTMENT_SAAS}/${id}`, payload);
    return response.data;
};

/**
 * Cancel appointment
 * This API cancels an existing appointment
 * @param {string} id - The appointment ID
 * @param {object} payload - The cancellation data
 * @returns {Promise} - Cancellation result
 */
export const cancelAppointmentApi = async (id, payload) => {
    const response = await instance.put(`${APPOINTMENT_ROUTES.CANCEL_APPOINTMENT_SAAS}/${id}`, payload);
    return response.data;
};

/**
 * Get mechanic details for assignment
 * This API fetches available mechanics for appointment assignment
 * @returns {Promise} - Mechanic details data
 */
export const getMechanicDetailsApi = async () => {
    const response = await instance.get(APPOINTMENT_ROUTES.MECHANIC_DETAILS);
    return response.data;
};

/**
 * Propose new date and time for appointment
 * This API proposes a new date and time for an appointment
 * @param {string} id - The appointment ID
 * @param {object} payload - The proposed date/time data
 * @returns {Promise} - Proposal result
 */
export const proposeDateTimeApi = async (id, payload) => {
    const response = await instance.post(`${APPOINTMENT_ROUTES.PROPOSE_DATE_TIME}/${id}`, payload);
    return response.data;
};

/**
 * Add service to appointment
 * This API adds a service to an existing appointment
 * @param {string} id - The appointment ID
 * @param {object} payload - The service data
 * @returns {Promise} - Add service result
 */
export const addServiceToAppointmentApi = async (id, payload) => {
    const response = await instance.post(`${APPOINTMENT_ROUTES.ADD_SERVICE}/${id}`, payload);
    return response.data;
};

/**
 * Delete service from appointment
 * This API removes a service from an appointment
 * @param {string} id - The appointment ID
 * @param {string} serviceId - The service ID to remove
 * @returns {Promise} - Delete service result
 */
export const deleteServiceFromAppointmentApi = async (id, serviceId) => {
    const response = await instance.delete(`${APPOINTMENT_ROUTES.DELETE_SERVICE}/${id}/${serviceId}`);
    return response.data;
};

/**
 * Update appointment note for customer
 * This API updates the note associated with an appointment for customer communication
 * @param {string} id - The appointment ID
 * @param {object} payload - The note data
 * @returns {Promise} - Update note result
 */
export const updateAppointmentNoteApi = async (id, payload) => {
    const response = await instance.put(`${APPOINTMENT_ROUTES.UPDATE_NOTE}/${id}`, payload);
    return response.data;
};

/**
 * Accept or decline reschedule request
 * This API handles the acceptance or decline of a reschedule request
 * @param {string} id - The appointment ID
 * @param {object} payload - The accept/decline data
 * @returns {Promise} - Accept/decline result
 */
export const rescheduleAcceptDeclineApi = async (id, payload) => {
    const response = await instance.put(`${APPOINTMENT_ROUTES.RESCHEDULE_ACCEPT_DECLINE}/${id}`, payload);
    return response.data;
};

/**
 * Get shop closing time based on drop off date
 * This API fetches the shop closing time for a given drop off date
 * @param {object} payload - The request payload
 * @param {string|dayjs.Dayjs} payload.dropOffDateTime - The drop off date time (will be converted to UTC)
 * @param {object} payload.cancelToken - Axios cancel token for request cancellation
 * @returns {Promise} - Shop closing time data
 */
export const getShopClosingTimeApi = async (payload) => {
    const response = await instance.post(
        APPOINTMENT_ROUTES.SHOP_CLOSING_TIME,
        {
            dropOffDateTime: payload.dropOffDateTime,
        },
        {
            cancelToken: payload.cancelToken,
        }
    );
    return response.data;
};
